import React, { useState, useEffect } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";

import SocialProofToast from "@/components/SocialProofToast";
import DarkModePopup from "@/components/DarkModePopup";
import WhatsAppFloatingMenu from "@/components/WhatsAppFloatingMenu";
import ChatBot from "@/components/ChatBot";
import CookieConsent from "@/components/CookieConsent";
import { NavLogo, GoogleS, NickLogo } from "@/components/BrandLogo";
import {
  MapPin, Phone, Calendar, MessageCircle, Linkedin,
  ChevronRight, Menu, X, Navigation, ArrowRight,
  Camera, Eye, Star, CheckCircle, Shield, Clock, Award,
  Search, Globe, Target, Moon, Battery, Leaf
} from "lucide-react";

const PLACE_ID = "ChIJ8Zku0V47KhMRb5aqDGqIbU0";
const GMAPS_VIEW = `https://www.google.com/maps/place/?q=place_id:${PLACE_ID}`;
const GMAPS_NAV = `https://www.google.com/maps/dir/?api=1&destination_place_id=${PLACE_ID}`;
const GMAPS_REVIEWS = `https://search.google.com/local/reviews?placeid=${PLACE_ID}`;
const WA_LINK = "https://wa.me/+393273839782";
const CALENDLY = "https://calendly.com/riccardorossi1984";

export { PLACE_ID, GMAPS_VIEW, GMAPS_NAV, GMAPS_REVIEWS, WA_LINK, CALENDLY };

// Custom Logo Component - schedabusiness.com Brand
export const SOSLogo = ({ size = "default", showText = true }) => {
  return <NavLogo />;
};

// Section Divider
export const SectionDivider = () => (
  <div className="py-8 flex items-center justify-center">
    <div className="flex items-center gap-4">
      <div className="w-16 md:w-32 h-0.5 bg-gradient-to-r from-transparent to-[#ff6b35]/30"></div>
      <svg className="w-10 h-10 opacity-60" viewBox="0 0 48 48" fill="none"><path d="M24 4C15.163 4 8 11.163 8 20C8 31 24 44 24 44C24 44 40 31 40 20C40 11.163 32.837 4 24 4Z" fill="#4285F4"/><circle cx="24" cy="20" r="8" fill="white"/><circle cx="24" cy="20" r="4" fill="#4285F4"/></svg>
      <div className="w-16 md:w-32 h-0.5 bg-gradient-to-l from-transparent to-[#ff6b35]/30"></div>
    </div>
  </div>
);

// Top Orange Bar
const TopBar = () => (
  <div className="bg-[#ff6b35] text-white py-2 px-4 flex items-center justify-center gap-4 text-sm">
    <div className="flex items-center gap-2">
      <Navigation className="w-4 h-4" />
      <span className="hidden sm:inline">Profilo sospeso? Scheda bloccata? Chiamami, ci penso io</span>
      <span className="sm:hidden">Problemi con la scheda? Chiamami</span>
    </div>
    <Button
      size="sm"
      className="bg-white text-[#ff6b35] hover:bg-gray-100 rounded-full px-4 py-1 h-auto text-xs font-medium"
      onClick={() => window.open(GMAPS_NAV, '_blank')}
      data-testid="localize-btn"
    >
      <MapPin className="w-3 h-3 mr-1" />
      Localizzami
    </Button>
    <span
      className="text-white/80 cursor-pointer hover:text-white"
      onClick={() => window.open(GMAPS_VIEW, '_blank')}
    >
      Vedi orari
    </span>
  </div>
);

// schedabusiness.com Logo — Google colors (Blu-Rosso-Giallo-Blu-Verde-Rosso)
export const SchedaBusinessLogo = ({ size = "default" }) => {
  const textClass = size === "small" ? "text-sm" : "text-base sm:text-lg";
  return (
    <span className={`${textClass} font-black tracking-tight leading-none whitespace-nowrap`} style={{ fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 40%, #D4AF37 60%, #B8942E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
      schedabusiness.com
    </span>
  );
};

// Navigation with 4 door links
const NavigationBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const doors = [
    { path: "https://www.schedabusiness.it/#deducibile", label: "Deducibile 100%", color: "bg-[#4285F4]", external: true },
    { path: "/start", label: "Start", color: "bg-[#EA4335]" },
    { path: "/plus", label: "Plus", color: "bg-[#FBBC04]" },
    { path: "/rooftop", label: "Rooftop", color: "bg-[#34A853]" },
    { path: "/academy", label: "Academy", color: "bg-[#D4AF37]", isAcademy: true },
  ];

  return (
    <nav className="bg-[#0c1220] border-b border-white/10 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-home-link" className="group relative rounded-xl p-[1.5px] overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-40 group-hover:opacity-70 transition-opacity rounded-xl"></div>
            <div className="relative bg-[#0c1220] rounded-[10px] px-3 py-1.5 flex flex-col items-center leading-none">
              <SchedaBusinessLogo size="default" />
              <span className="text-[9px] sm:text-[10px] font-bold italic mt-0.5 tracking-wide text-white text-center block" style={{ fontFamily: "'Poppins', sans-serif", textShadow: '0 0 8px rgba(255,255,255,0.4)' }}>
                se non ti trovano, non esisti
              </span>
              <div className="flex items-center gap-1 mt-1">
                <NickLogo size={14} showPayoff={false} />
                <span className="text-[9px] font-black italic" style={{ fontFamily: "'Poppins',sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Cares</span>
              </div>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-5 ml-6">
            {doors.map((d) =>
              d.external ? (
                <a
                  key={d.path}
                  href={d.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative w-12 h-12 bg-[#4285F4] hover:bg-[#3574d4] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 flex-shrink-0"
                  data-testid="nav-deducibile-link"
                >
                  <div className="text-center">
                    <span className="text-white text-[9px] font-black block leading-none">Detraibile</span>
                    <span className="text-white text-[10px] font-black block leading-none">100%</span>
                  </div>
                  {/* PayPal badge */}
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-black rounded-full flex items-center justify-center border border-white/30">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
                  </div>
                </a>
              ) : d.isAcademy ? (
                <Link
                  key={d.path}
                  to={d.path}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                    location.pathname === d.path ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid="nav-academy-link"
                >
                  <NickLogo size={22} showPayoff={false} />
                  <span className="font-black italic" style={{ fontFamily: "'Poppins',sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Cares</span>
                </Link>
              ) : (
                <Link
                  key={d.path}
                  to={d.path}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                    location.pathname === d.path ? 'text-white' : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid={`nav-${d.path.slice(1)}-link`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${d.color}`} style={{ aspectRatio: '1' }}></span>
                  <span className={d.typewriter ? 'font-typewriter' : ''}>{d.label}</span>
                </Link>
              )
            )}
            <Link
              to="/deducibile100"
              className="group relative rounded-full p-[2px] text-sm font-bold transition-all inline-flex items-center overflow-hidden hover:scale-105"
              data-testid="nav-vt-cta"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-80 group-hover:opacity-100 transition-opacity rounded-full"></div>
              <div className="relative bg-[#0a0a0a] rounded-full px-5 py-2 flex items-center gap-1.5">
                <Camera className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-black italic" style={{ fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', textShadow: 'none' }}>Virtual Tour</span>
              </div>
            </Link>
          </div>

          <button className="md:hidden text-white" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-4">
              {doors.map((d) =>
                d.external ? (
                  <a
                    key={d.path}
                    href={d.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className={`w-3 h-3 rounded-full ${d.color}`}></span>
                    <span>{d.label}</span>
                  </a>
                ) : (
                  <Link
                    key={d.path}
                    to={d.path}
                    className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className={`w-3 h-3 rounded-full ${d.color}`}></span>
                    <span className={d.typewriter ? 'font-typewriter' : ''}>{d.label}</span>
                  </Link>
                )
              )}
              <Link
                to="/deducibile100"
                onClick={() => setIsOpen(false)}
                className="group relative rounded-full p-[2px] text-sm font-bold transition-all inline-flex items-center justify-center overflow-hidden w-full"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-80 rounded-full"></div>
                <div className="relative bg-[#0a0a0a] rounded-full w-full px-5 py-2.5 flex items-center justify-center gap-1.5">
                  <span className="font-black italic" style={{ fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Virtual Tour</span>
                </div>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer
export const Footer = () => (
  <footer className="bg-gray-900 text-white">
    {/* Footer content */}
    <div className="py-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Left — Brand name only */}
        <div className="flex items-center gap-3">
          <div className="flex flex-col leading-none">
            <span className="font-black text-sm" style={{ fontFamily: "'Poppins', sans-serif", background: 'linear-gradient(135deg, #D4AF37 0%, #F5E6A3 40%, #D4AF37 60%, #B8942E 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              schedabusiness.com
            </span>
          </div>
        </div>
        {/* Center — Copyright + payoff */}
        <div className="text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Riccardo Rossi - Consulente Business Profile Siena | Fotografo Street View Certificato
          </p>
          <p className="text-sm font-black italic text-white mt-1" style={{ fontFamily: "'Poppins', sans-serif", textShadow: '0 0 12px rgba(255,255,255,0.6), 0 0 24px rgba(255,255,255,0.3)' }}>
            se non ti trovano, non esisti
          </p>
        </div>
        {/* Right — Icons + NickLogo senza payoff */}
        <div className="flex items-center gap-4">
          <a href="https://maps.google.com/localguides/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#ff6b35] transition-colors" data-testid="local-guides-link" title="Local Guides dal 2015">
            <MapPin className="w-5 h-5" />
          </a>
          <a href="https://www.linkedin.com/in/riccardorossi1984googletnx/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#ff6b35] transition-colors">
            <Linkedin className="w-5 h-5" />
          </a>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#ff6b35] transition-colors">
            <MessageCircle className="w-5 h-5" />
          </a>
          <div className="flex items-center gap-1">
            <NickLogo size={20} showPayoff={false} />
            <span className="text-xs font-black italic" style={{ fontFamily: "'Poppins',sans-serif", background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Cares</span>
          </div>
        </div>
      </div>

      {/* Dark Mode — Ci prendiamo cura di te */}
      <div className="mt-6 pt-5 border-t border-gray-800">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 mb-4 text-[11px] text-gray-500">
          <span><strong className="text-[#FBBC04]">51 recensioni reali</strong> — tutte a 5 stelle su Google</span>
          <span><strong className="text-[#4285F4]">7.000+ foto</strong> pubblicate</span>
          <span><strong className="text-[#34A853]">52M views</strong> generate</span>
          <span>Fotografo Google Street View Certificato dal 2016</span>
        </div>
        <a href="/darkmode" className="group flex flex-col sm:flex-row items-center justify-center gap-3 py-3 px-5 -mx-4 rounded-xl hover:bg-white/[0.03] transition-colors" data-testid="dark-mode-footer-link">
          <div className="flex items-center gap-2">
            <Moon className="w-4 h-4 text-[#FBBC04]" />
            <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
              Questo sito e' <strong className="text-[#FBBC04]">dark by design</strong>
            </span>
          </div>
          <span className="text-xs text-gray-500">
            <Battery className="w-3.5 h-3.5 inline mr-1 text-[#34A853]" />-47% energia
            <Eye className="w-3.5 h-3.5 inline mx-1 text-[#4285F4]" />-60% stress occhi
            <Leaf className="w-3.5 h-3.5 inline mx-1 text-[#34A853]" />-CO2
          </span>
          <span className="text-[10px] text-gray-600 group-hover:text-[#ff6b35] transition-colors">
            Scopri perche' e come attivarlo ovunque &rarr;
          </span>
        </a>
      </div>

      {/* Made in Toscana */}
      <div className="mt-4 pt-4 border-t border-gray-800">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-4 rounded-sm overflow-hidden flex-shrink-0" viewBox="0 0 30 20"><rect width="10" height="20" fill="#009246"/><rect x="10" width="10" height="20" fill="#fff"/><rect x="20" width="10" height="20" fill="#CE2B37"/></svg>
            <span className="text-gray-300 text-sm font-medium">schedabusiness.com e' un marchio <strong className="text-white">Made in Toscana</strong> — <strong className="text-white">Italy</strong></span>
          </div>
          <a
            href="https://www.regione.toscana.it/bandi-aperti?sortBy=desc&orderBy=modifiedDate"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 rounded-full px-3 py-1.5 transition-colors group"
            data-testid="regione-toscana-link"
          >
            <svg className="h-6 w-6 flex-shrink-0" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="11" fill="#cc0000" stroke="#fff" strokeWidth="1"/>
              <path d="M8 16c2-4 4-6 6-8M7 14c1.5-2.5 3-4 5-6M12 8l1.5 2.5L12 16" stroke="#fff" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
            </svg>
            <span className="text-xs text-gray-400 group-hover:text-white transition-colors">Regione Toscana</span>
          </a>
          <div className="flex items-center gap-2" data-testid="gallo-nero">
            <img src="https://customer-assets.emergentagent.com/job_c2e7986e-611e-40f0-8a3b-2eb84b9e2249/artifacts/6b6qdxci_image.png" alt="Gallo Nero — Chianti Classico" className="h-7 w-7 rounded-full object-cover" />
            <span className="text-xs text-gray-500">Gallo Nero</span>
          </div>
        </div>
        {/* In collaborazione con Google Street View */}
        <div className="flex items-center justify-center gap-3 py-4 border-t border-white/5 mt-4" data-testid="footer-gsv-badge">
          <span className="text-gray-600 text-[10px] uppercase tracking-wider">in collaborazione con</span>
          <a href="https://www.google.com/streetview/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-full px-4 py-1.5 transition-colors border border-white/10">
            <svg className="w-5 h-5" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="22" fill="#fff" stroke="#4285F4" strokeWidth="2"/>
              <circle cx="24" cy="16" r="6" fill="#4285F4"/>
              <path d="M14 36c0-5.523 4.477-10 10-10s10 4.477 10 10" fill="#34A853"/>
              <path d="M30 20l6-4v12l-6-4z" fill="#FBBC04"/>
            </svg>
            <span className="text-white text-xs font-bold">Google Street View</span>
            <span className="text-[#4285F4] text-[10px] font-black bg-[#4285F4]/10 px-1.5 py-0.5 rounded">TRUSTED</span>
          </a>
        </div>
        <p className="text-gray-500 text-xs text-center">
          Assistenza Business Profile Siena | Sospensione scheda | Recupero scheda attivita' |
          Ripristino profilo Business | Supporto tecnico | Consulente Maps Siena |
          <a href="https://maps.google.com/localguides/" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff6b35] transition-colors"> Local Guide dal 2015</a>
        </p>
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              const isLight = document.documentElement.getAttribute("data-theme") === "light";
              if (isLight) {
                document.documentElement.removeAttribute("data-theme");
                localStorage.setItem("sb-theme", "dark");
              } else {
                document.documentElement.setAttribute("data-theme", "light");
                localStorage.setItem("sb-theme", "light");
              }
              window.location.reload();
            }}
            className="text-gray-600 hover:text-gray-400 text-xs transition-colors"
            data-testid="theme-toggle-footer"
          >
            Cambia tema (dark/light)
          </button>
        </div>
      </div>
    </div>
    </div>
  </footer>
);

// Efficacia del Profilo Ring Component (like Google's real one)
const EfficaciaRing = ({ percentage = 100, size = 48, color = "#34A853", label }) => {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (percentage / 100) * c;
  return (
    <div className="flex items-center gap-3">
      <svg width={size} height={size} className="flex-shrink-0">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#374151" strokeWidth="5" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: 'stroke-dashoffset 1.5s ease-out' }}
        />
      </svg>
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
};

// Contact Section - Shared across pages
export const ContactSection = () => (
  <>
    {/* Efficacia del Profilo - Before/After */}
    <section className="py-14 bg-[#0c1220]" data-testid="efficacia-profilo-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 font-google">
            Efficacia del <span className="text-[#ff6b35]">profilo</span>: da scadente a massima
          </h2>
          <p className="text-gray-400 text-base md:text-lg">Ecco cosa succede alla TUA scheda dopo il nostro intervento</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 items-center">
          {/* Before */}
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-4 font-bold">Prima</p>
            <div className="bg-gray-900 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-gray-300 text-sm">Efficacia del profilo</span>
                <EfficaciaRing percentage={35} size={36} color="#FBBC04" />
              </div>
              <p className="text-yellow-500 text-xs">Completa le informazioni</p>
            </div>
            <ul className="mt-4 space-y-2">
              {["Informazioni incomplete", "Foto mancanti o vecchie", "Zero recensioni gestite", "Nessun post pubblicato"].map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                  <svg className="w-4 h-4 text-red-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/></svg>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Arrow */}
          <div className="flex justify-center items-center py-2 md:py-0">
            <div className="bg-[#ff6b35] w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-lg">
              <ArrowRight className="w-6 h-6 md:w-7 md:h-7 text-white rotate-90 md:rotate-0" />
            </div>
          </div>

          {/* After */}
          <div className="bg-white/5 rounded-2xl p-6 border-2 border-green-500/30 ring-1 ring-green-500/10">
            <p className="text-xs uppercase tracking-wider text-green-400 mb-4 font-bold">Dopo schedabusiness</p>
            <div className="bg-gray-900 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-gray-300 text-sm">Efficacia del profilo</span>
                <EfficaciaRing percentage={100} size={36} color="#34A853" />
              </div>
              <p className="text-green-400 text-xs font-bold">Massima</p>
            </div>
            <ul className="mt-4 space-y-2">
              {["Tutte le info ottimizzate", "Foto professionali caricate", "Recensioni gestite con strategia", "Post e offerte pubblicati"].map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* Google Workspace Email Section */}
    <section className="py-14 bg-[#0a0e1a]" data-testid="workspace-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-6 md:p-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="relative grid md:grid-cols-[1fr,auto] gap-6 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none"><path d="M20 18H4V8l8 5 8-5v10zm0-12H4l8 5 8-5z" fill="#EA4335"/><path d="M4 6v12h16V6" fill="none" stroke="#4285F4" strokeWidth="1.5"/></svg>
                <span className="text-white font-bold font-google">Il tassello finale</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3 font-google">
                Crea il TUO indirizzo Gmail professionale
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Usi ancora la TUA Gmail personale per l'attivita'? <strong className="text-white">E' un errore.</strong> Mai
                mischiare personale e professionale. Con Google Workspace crei
                <strong className="text-white"> tuobrand@gmail.com</strong> — un indirizzo che utilizza un dominio personalizzato,
                ti fa sentire parte dell'ecosistema Google e si collega direttamente a <strong className="text-amber-300">Google Ads</strong>.
              </p>
              <p className="text-gray-500 text-xs leading-relaxed mb-4">
                Creando il TUO account Workspace ti impegni a curare la TUA presenza su Google.
                E' il primo passo per chi fa sul serio.
              </p>
              <ul className="space-y-2 mb-5">
                {[
                  "Il TUO brand su Gmail (tuobrand@gmail.com)",
                  "Collegamento diretto a Google Ads",
                  "Efficacia profilo: tassello finale per il massimo",
                  "30 GB di spazio, Google Drive, Calendar inclusi",
                ].map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
              <Button
                className="bg-[#4285F4] hover:bg-[#3367D6] text-white rounded-full px-6 font-google"
                onClick={() => window.open('https://workspace.google.com/intl/it/contact/', '_blank')}
                data-testid="workspace-cta"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M20 18H4V8l8 5 8-5v10zm0-12H4l8 5 8-5z"/></svg>
                Crea il TUO Account Workspace
              </Button>
            </div>
            <div className="hidden md:flex flex-col items-center gap-3 pr-4">
              <EfficaciaRing percentage={100} size={80} color="#34A853" />
              <span className="text-green-400 text-xs font-bold">Efficacia massima</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Google Fonts - Regalo per il brand */}
    <section className="py-14 bg-[#0c1220]" data-testid="google-fonts-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/5 rounded-3xl p-6 md:p-8 border border-white/10">
          <div className="grid md:grid-cols-[1fr,auto] gap-6 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-6 h-6" viewBox="0 0 24 24"><path d="M9.93 13.5h4.14L12 7.98z" fill="#4285F4"/><path d="M20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-4.05 16.5l-1.14-3H9.17l-1.12 3H5.96l5.11-13h1.86l5.11 13h-2.09z" fill="#EA4335"/></svg>
                <span className="font-bold text-white text-sm font-google">Un altro regalo per te</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3 font-google">
                Trova il font perfetto per il TUO brand
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Google Fonts ti permette di scegliere tra oltre 1.500 font open source e di vedere in anteprima
                come appare il TUO nome. Il font giusto comunica professionalita'.
              </p>
              <Button
                className="bg-white text-gray-900 hover:bg-gray-200 rounded-full px-6 font-google"
                onClick={() => window.open('https://fonts.google.com/', '_blank')}
                data-testid="google-fonts-cta"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M9.93 13.5h4.14L12 7.98z"/><path d="M20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-4.05 16.5l-1.14-3H9.17l-1.12 3H5.96l5.11-13h1.86l5.11 13h-2.09z"/></svg>
                Esplora Google Fonts
              </Button>
            </div>
            <div className="hidden md:block text-center">
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <p className="text-xs text-gray-500 mb-2">Anteprima</p>
                <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>TuoBrand</p>
                <p className="text-sm text-gray-500 mt-1">Poppins · Bold</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Dove Siamo e Contatti — via Place ID */}
    <section id="contatti" className="py-16 bg-[#080c18] text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#4285F4]/15 text-[#4285F4] px-4 py-1.5 rounded-full font-bold text-xs mb-4">
            <MapPin className="w-4 h-4" /> DOVE SIAMO
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Poppins',sans-serif" }}>
            SchedaBusiness.com
          </h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* LEFT — Mappa con card overlay (3/5) */}
          <div className="lg:col-span-3">
            <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/10 relative">
              <iframe
                src="https://maps.google.com/maps?ll=43.33,11.12&z=8&output=embed&hl=it"
                width="100%" height="100%" style={{ border: 0, minHeight: '380px' }}
                allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                title="schedabusiness.com — Google Business Profile Map"
              ></iframe>
              {/* Card bianca sovrapposta — stile Google Place (Zanfa Bros) — in alto a sinistra */}
              <a
                href={GMAPS_VIEW}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-3 left-3 bg-white rounded-lg shadow-xl p-3 max-w-[280px] hover:shadow-2xl transition-shadow z-20 block"
                data-testid="dove-siamo-place-card"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[#1a1a1a] font-bold text-base leading-tight">SchedaBusiness.com</p>
                    <p className="text-[#70757a] text-xs leading-tight mt-0.5">le casacce, 110, 53034 Le Grazie SI, Italia</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-[#4285F4]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
                    <svg className="w-5 h-5 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-1.5">
                  <span className="text-[#e8710a] font-bold text-sm">5,0</span>
                  <span className="text-[#e8710a] text-sm">&#9733;</span>
                  <span className="text-[#4285F4] text-xs font-medium">(51)</span>
                </div>
              </a>
              {/* Card dark in basso — azioni stile Google */}
              <div
                className="absolute bottom-0 left-0 right-0 bg-[#202124] z-10 border-t border-white/10"
              >
                <div className="px-4 pt-4 pb-3">
                  <a href={GMAPS_VIEW} target="_blank" rel="noopener noreferrer" className="block hover:underline">
                    <p className="text-white font-bold text-lg leading-tight">SchedaBusiness.com</p>
                  </a>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-white font-bold text-sm">5,0</span>
                    <span className="text-[#fbbc04] text-sm tracking-tight">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
                    <a href={GMAPS_REVIEWS} target="_blank" rel="noopener noreferrer" className="text-[#8ab4f8] text-sm hover:underline">51 recensioni</a>
                  </div>
                  <p className="text-[#9aa0a6] text-sm mt-0.5">Fotografo commerciale a Le Grazie</p>
                </div>
                <div className="flex flex-wrap gap-2 px-4 pb-4">
                  <a href="https://schedabusiness.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-[#303134] hover:bg-[#3c4043] text-[#8ab4f8] text-xs font-medium px-3 py-2 rounded-full transition-colors border border-[#5f6368]/50">
                    <Globe className="w-3.5 h-3.5" /> Sito web
                  </a>
                  <a href={GMAPS_NAV} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-[#303134] hover:bg-[#3c4043] text-[#8ab4f8] text-xs font-medium px-3 py-2 rounded-full transition-colors border border-[#5f6368]/50">
                    <Navigation className="w-3.5 h-3.5" /> Indicazioni
                  </a>
                  <a href={GMAPS_REVIEWS} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-[#303134] hover:bg-[#3c4043] text-[#8ab4f8] text-xs font-medium px-3 py-2 rounded-full transition-colors border border-[#5f6368]/50">
                    <Star className="w-3.5 h-3.5" /> Recensioni
                  </a>
                  <a href="tel:+393420380578" className="inline-flex items-center gap-1.5 bg-[#303134] hover:bg-[#3c4043] text-[#8ab4f8] text-xs font-medium px-3 py-2 rounded-full transition-colors border border-[#5f6368]/50">
                    <Phone className="w-3.5 h-3.5" /> Chiama
                  </a>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <a
                href={GMAPS_VIEW}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#4285F4] hover:text-[#93c5fd] text-sm font-bold transition-colors"
                data-testid="show-larger-map"
              >
                <Globe className="w-4 h-4" />
                Scheda Business.com — Apri su Google Maps
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <span className="text-gray-600 text-[10px] hidden sm:block">collegata via Place ID</span>
            </div>
          </div>

          {/* RIGHT — Contatti + Place ID showcase (2/5) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Business Card */}
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5" data-testid="sbp-business-card">
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-10 h-10 flex-shrink-0" viewBox="0 0 48 48" fill="none"><path d="M24 4C15.163 4 8 11.163 8 20C8 31 24 44 24 44C24 44 40 31 40 20C40 11.163 32.837 4 24 4Z" fill="#4285F4"/><circle cx="24" cy="20" r="8" fill="white"/><circle cx="24" cy="20" r="4" fill="#4285F4"/></svg>
                <div>
                  <h3 className="text-lg font-black text-white">schedabusiness.com</h3>
                  <div className="flex items-center gap-1">
                    <span className="text-white font-bold text-sm">5,0</span>
                    <span className="text-yellow-400 text-sm">★★★★★</span>
                    <a href={GMAPS_REVIEWS} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline ml-1">50 recensioni</a>
                  </div>
                </div>
              </div>
              <div className="space-y-2.5">
                <a href={GMAPS_VIEW} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-gray-300 hover:text-[#4285F4] transition-colors text-sm" data-testid="gmaps-link">
                  <MapPin className="w-4 h-4 flex-shrink-0" /><span>Vedi la scheda su Google Maps</span>
                </a>
                <a href="tel:+393420380578" className="flex items-center gap-2.5 text-gray-300 hover:text-[#4285F4] transition-colors text-sm">
                  <Phone className="w-4 h-4 flex-shrink-0" /><span>(+39) 342 038 0578</span>
                </a>
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-gray-300 hover:text-[#4285F4] transition-colors text-sm">
                  <MessageCircle className="w-4 h-4 flex-shrink-0" /><span>WhatsApp</span>
                </a>
                <a href="https://share.google/plYYOroJG18abeUnq" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-gray-300 hover:text-[#4285F4] transition-colors text-sm" data-testid="google-share-link">
                  <Star className="w-4 h-4 flex-shrink-0" /><span>Condividi la scheda Google</span>
                </a>
              </div>
              {/* Badges */}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-white/10">
                <img src="https://customer-assets.emergentagent.com/job_sos-activity-cards/artifacts/wr288sey_Google-Street-View-Trusted-Badge-2021.png" alt="Google Street View Trusted" className="h-9 object-contain" />
                <a href="https://www.linkedin.com/in/riccardorossi1984googletnx/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 rounded-full px-3 py-1.5 transition-colors">
                  <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                  <span className="text-xs text-gray-300">Premium</span>
                </a>
              </div>
            </div>

            {/* PLACE ID SHOWCASE — educativo */}
            <div className="bg-white/[0.03] border border-[#FBBC04]/20 rounded-2xl p-5" data-testid="place-id-showcase">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-[#FBBC04]" />
                <span className="text-sm font-black text-white">Cos'e' un Place ID?</span>
              </div>
              <p className="text-gray-400 text-xs leading-relaxed mb-3">
                Ogni attivita' su Google ha un <strong className="text-white">identificativo unico</strong>. Collegare il sito al Place ID (e viceversa) e' fondamentale per la visibilita' locale. Molte aziende non lo fanno.
              </p>
              {/* Il nostro Place ID */}
              <div className="bg-black/30 rounded-xl p-3 border border-white/10 mb-3">
                <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider font-bold">Il nostro Place ID</p>
                <div className="flex items-center gap-2">
                  <code className="text-[#FBBC04] text-xs font-mono bg-[#FBBC04]/10 px-2 py-1 rounded flex-1 overflow-x-auto">{PLACE_ID}</code>
                  <button
                    onClick={() => {navigator.clipboard.writeText(PLACE_ID); }}
                    className="text-gray-500 hover:text-white text-xs px-2 py-1 bg-white/5 rounded transition-colors"
                    data-testid="copy-place-id"
                  >
                    Copia
                  </button>
                </div>
              </div>
              {/* Il TUO Place ID */}
              <div className="bg-white/[0.02] rounded-xl p-3 border border-dashed border-white/10">
                <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider font-bold">Il TUO Place ID</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-black/20 rounded px-2 py-1.5 text-gray-600 text-xs font-mono border border-white/5">
                    ChIJ_________________
                  </div>
                </div>
                <p className="text-gray-500 text-[10px] mt-2">
                  Non sai qual e'? <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="text-[#4285F4] hover:underline font-bold">Te lo trovo io</a>
                </p>
              </div>
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={GMAPS_VIEW} target="_blank" rel="noopener noreferrer" className="flex-1 bg-[#4285F4] hover:bg-[#3574d4] text-white rounded-full py-2.5 text-sm font-bold text-center transition-colors inline-flex items-center justify-center gap-2" data-testid="cta-vedi-scheda">
                <MapPin className="w-4 h-4" /> Vedi la Scheda
              </a>
              <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-full py-2.5 text-sm font-bold text-center transition-colors inline-flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" /> Prenota Chiamata
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);

// Main Layout wrapper
export default function Layout() {
  useEffect(() => {
    const theme = localStorage.getItem("sb-theme");
    if (theme === "light") {
      document.documentElement.setAttribute("data-theme", "light");
    }
    // Hide Emergent badge
    const hideBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (badge) { badge.style.cssText = "display:none!important;visibility:hidden!important;opacity:0!important;width:0!important;height:0!important;overflow:hidden!important;position:fixed!important;pointer-events:none!important;"; return true; }
      return false;
    };
    hideBadge();
    if (!hideBadge()) { const iv = setInterval(() => { if (hideBadge()) clearInterval(iv); }, 500); setTimeout(() => clearInterval(iv), 5000); }
    // Crossfade handler for .it links
    const handler = (e) => {
      const link = e.target.closest('a[href*="schedabusiness.it"]');
      if (link && link.target === '_blank') {
        e.preventDefault();
        document.getElementById('root').classList.add('crossfade-out');
        setTimeout(() => {
          window.open(link.href, '_blank');
          document.getElementById('root').classList.remove('crossfade-out');
        }, 300);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div className="min-h-screen bg-[#0c1220]">
      <TopBar />
      <NavigationBar />
      <Outlet />
      <Footer />
      {/* Floating WhatsApp menu — dark design with survey */}
      <WhatsAppFloatingMenu />
      <ChatBot />
      <SocialProofToast />
      <DarkModePopup />
      <CookieConsent />
    </div>
  );
}
